import React from 'react'
import { useQuery } from 'react-query'
import { configApi, userApi, auditApi } from '../services/api'
import { 
  Settings, 
  Users, 
  FileText, 
  Activity,
  Database,
  CreditCard,
  Cloud,
  Server
} from 'lucide-react'

const DashboardPage: React.FC = () => {
  const { data: stripeConfigs } = useQuery('stripe-configs', () => configApi.getByCategory('stripe'))
  const { data: redisConfigs } = useQuery('redis-configs', () => configApi.getByCategory('redis'))
  const { data: storageConfigs } = useQuery('storage-configs', () => configApi.getByCategory('storage'))
  const { data: databaseConfigs } = useQuery('database-configs', () => configApi.getByCategory('database'))
  const { data: users } = useQuery('users', userApi.list)
  const { data: auditLogs } = useQuery('recent-audit-logs', () => auditApi.list({ limit: 5 }))

  const stats = [
    {
      name: 'Total Configurations',
      value: (stripeConfigs?.length || 0) + (redisConfigs?.length || 0) + (storageConfigs?.length || 0) + (databaseConfigs?.length || 0),
      icon: Settings,
      color: 'bg-blue-500',
    },
    {
      name: 'Admin Users',
      value: users?.length || 0,
      icon: Users,
      color: 'bg-green-500',
    },
    {
      name: 'Recent Activities',
      value: auditLogs?.logs?.length || 0,
      icon: Activity,
      color: 'bg-purple-500',
    },
    {
      name: 'System Health',
      value: 'Good',
      icon: FileText,
      color: 'bg-emerald-500',
    },
  ]

  const configCategories = [
    {
      name: 'Stripe Configuration',
      description: 'SaaS subscription payment settings',
      icon: CreditCard,
      count: stripeConfigs?.length || 0,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      name: 'Redis Configuration',
      description: 'Cache and session storage settings',
      icon: Database,
      count: redisConfigs?.length || 0,
      color: 'bg-red-100 text-red-600',
    },
    {
      name: 'Storage Configuration',
      description: 'Google Cloud Storage settings',
      icon: Cloud,
      count: storageConfigs?.length || 0,
      color: 'bg-yellow-100 text-yellow-600',
    },
    {
      name: 'Database Configuration',
      description: 'PostgreSQL database settings',
      icon: Server,
      count: databaseConfigs?.length || 0,
      color: 'bg-green-100 text-green-600',
    },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          System overview and configuration management
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.name} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`${stat.color} rounded-md p-3`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stat.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Configuration Categories */}
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">Configuration Categories</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {configCategories.map((category) => {
            const Icon = category.icon
            return (
              <div key={category.name} className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className={`${category.color} rounded-md p-3`}>
                        <Icon className="h-6 w-6" />
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {category.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {category.description}
                      </p>
                      <p className="text-sm font-medium text-gray-600 mt-2">
                        {category.count} configuration{category.count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Recent Activity
          </h3>
          {auditLogs?.logs && auditLogs.logs.length > 0 ? (
            <div className="flow-root">
              <ul className="-mb-8">
                {auditLogs.logs.map((log, logIdx) => (
                  <li key={log.id}>
                    <div className="relative pb-8">
                      {logIdx !== auditLogs.logs.length - 1 ? (
                        <span
                          className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                          aria-hidden="true"
                        />
                      ) : null}
                      <div className="relative flex space-x-3">
                        <div>
                          <span className="h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center ring-8 ring-white">
                            <Activity className="h-4 w-4 text-white" />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                          <div>
                            <p className="text-sm text-gray-500">
                              <span className="font-medium text-gray-900">
                                {log.adminUser?.firstName} {log.adminUser?.lastName}
                              </span>{' '}
                              {log.action.toLowerCase()}d {log.resourceType}
                            </p>
                          </div>
                          <div className="text-right text-sm whitespace-nowrap text-gray-500">
                            {new Date(log.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No recent activity</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
